import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GoogleLogo, Play } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';
import { AUTH_PAGE } from '@/constants/testIds';
import api from '@/lib/api';
import Footer from '@/components/Footer';

export default function Login() {
    const { user, loading, checkAuth } = useAuth();
    const [demoLoading, setDemoLoading] = useState(false);
    const [demoAdminLoading, setDemoAdminLoading] = useState(false);

    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleDemo = async () => {
        setDemoLoading(true);
        try {
            await api.post('/auth/demo-login');
            await checkAuth();
            window.location.href = '/dashboard';
        } catch {
            setDemoLoading(false);
        }
    };

    const handleDemoAdmin = async () => {
        setDemoAdminLoading(true);
        try {
            await api.post('/auth/demo-admin-login');
            await checkAuth();
            window.location.href = '/settings/dictionary';
        } catch {
            setDemoAdminLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="flex-1 grid md:grid-cols-2">
                <div className="hidden md:flex flex-col justify-between px-14 py-16 bg-primary text-primary-foreground">
                    <div className="flex items-center gap-3">
                        <TanowMark size={40} tone="dark" />
                        <div>
                            <div className="font-heading text-2xl leading-none">Tanow</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 mt-1">Tanow Health · Family care, coordinated.</div>
                        </div>
                    </div>
                    <div className="max-w-md">
                        <h1 className="font-heading text-5xl leading-[1.05] tracking-tight text-white">
                            Nothing falls through the cracks.
                        </h1>
                        <p className="mt-6 text-white/80 leading-relaxed">
                            One warm place to keep track of Mom&apos;s medicines, Dad&apos;s follow-ups, and the questions
                            you didn&apos;t want to forget at the next visit. Family care, coordinated.
                        </p>
                    </div>
                    <div className="text-xs text-white/60">© Tanow Health</div>
                </div>

                <div className="flex items-center justify-center px-6 py-16">
                    <div className="w-full max-w-md">
                        <div className="mb-8">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Sign in</div>
                            <h2 className="font-heading text-4xl mt-2 text-slate-900">Welcome back</h2>
                            <p className="text-slate-600 mt-3 leading-relaxed">
                                Tanow is invite-only right now. If your admin has invited you, sign in with the same Google account.
                            </p>
                        </div>
                        <Button
                            data-testid={AUTH_PAGE.googleButton}
                            onClick={handleLogin}
                            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base"
                        >
                            <GoogleLogo size={18} weight="bold" className="mr-2" />
                            Continue with Google
                        </Button>

                        <div className="mt-3 text-center">
                            <a
                                data-testid="login-request-invite"
                                href="mailto:jizelle@gmail.com?subject=Tanow%20invite%20request&body=Hi%20Jizelle%2C%0A%0AI%27d%20like%20an%20invite%20to%20Tanow%20for%3A%0AName%3A%0AEmail%3A%0AWhat%20I%20do%3A%0A%0AThanks!"
                                className="text-sm text-primary hover:underline"
                            >
                                Don&apos;t have an invite yet? Request one →
                            </a>
                        </div>

                        <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
                            <div className="flex-1 h-px bg-slate-200" />
                            <span>or just look around</span>
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-4 space-y-3">
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold mb-1">Family view</div>
                                <div className="text-[13px] text-amber-900 leading-relaxed mb-2">
                                    Explore Tanow as the coordinator for two fictional families (Dela Cruz &amp; Santos).
                                </div>
                                <Button
                                    data-testid="login-demo-button"
                                    onClick={handleDemo}
                                    disabled={demoLoading}
                                    variant="outline"
                                    className="w-full h-11 rounded-full border-amber-300 text-amber-900 hover:bg-amber-100"
                                >
                                    <Play size={15} weight="fill" className="mr-2" />
                                    {demoLoading ? 'Loading demo…' : 'Try the family view'}
                                </Button>
                            </div>
                            <div className="border-t border-amber-200 pt-3">
                                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold mb-1">Admin view — read only</div>
                                <div className="text-[13px] text-amber-900 leading-relaxed mb-2">
                                    See the admin side: data dictionary, tagged data, lab catalog, rules &amp; activity — with only the fictional families.
                                </div>
                                <Button
                                    data-testid="login-demo-admin-button"
                                    onClick={handleDemoAdmin}
                                    disabled={demoAdminLoading}
                                    variant="outline"
                                    className="w-full h-11 rounded-full border-amber-300 text-amber-900 hover:bg-amber-100"
                                >
                                    <Play size={15} weight="fill" className="mr-2" />
                                    {demoAdminLoading ? 'Loading demo…' : 'Try the admin view'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
